/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import useBaseService from '../../../hooks/services/useBaseService'
import useLoader from '../../../hooks/useLoader'
import useNotification from '../../../hooks/useNotification'
import { entities } from '../../../constants/entities'
import MainLayout from '../../../layouts/MainLayout'
import PageHeader from '../../../components/PageHeader'
import { faFilePdf } from '@fortawesome/free-solid-svg-icons'
import useForm from '../../../hooks/useForm'
import { cleanPayload, setDefaultFieldValues } from '../../../utils/objectUtils'
import SimpleBody from '../../../components/forms/SimpleBody'
import SectionHeader from '../../../components/forms/SectionHeader'
import Form from '../../../components/forms/Form'
import { filterFields } from './filters'
import useRemoteSelectOptions from '../../../hooks/useRemoteSelectOptions'
import Button from '../../../components/Button'
import { Alert, Box } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useOrgan } from '../../../hooks/useOrgan'
import { useReport } from '../../../context/ReportContext'

const ProductExtractReport = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { selectedOrgan } = useOrgan()

  const { setExportId, isExporting } = useReport()

  const [filtersSection] = filterFields
  const { formData, handleChange, setFormData } = useForm(
    setDefaultFieldValues([filtersSection]),
    [filtersSection]
  )

  // Se ao menos um tiver preenchido marcar como true
  const [hasFilled, setHasFilled] = useState(false)

  const { get: baseServiceGet } = useBaseService(navigate)
  const { hideLoader, showLoader } = useLoader()
  const { showNotification } = useNotification()

  const documentTypeOptions = useRemoteSelectOptions({
    queryKey: ['documentTypes'],
    entityPath: entities.documentsTypes.get,
    baseServiceGet,
    fieldId: 'filter.document_type_id',
    formData,
    setFormData,
    isMulti: true,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    mapToOption: (item) => ({
      value: item.id,
      label: item.name,
    }),
  })

  const supplierSelect = useRemoteSelectOptions({
    queryKey: ['suppliers'],
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    baseServiceGet,
    entityPath: entities.suppliers.get,
    columnDataBase: ['id', 'alias', 'name'],
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.id} - ${item.alias} - ${item.name}`,
    }),
    fieldId: 'filter.supplier_id',
    formData,
    setFormData,
    isMulti: true,
    refetchOnWindowFocus: true,
  })

  const productSelect = useRemoteSelectOptions({
    queryKey: ['products'],
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    baseServiceGet,
    entityPath: entities.products.forSelection.get,
    columnDataBase: ['id', 'name'],
    mapToOption: (item) => ({
      value: item.id,
      label: `${item.id} - ${item.name}`,
    }),
    fieldId: 'filter.product_id',
    formData,
    setFormData,
    isMulti: true,
    refetchOnWindowFocus: true,
  })

  const typesOptions = useRemoteSelectOptions({
    queryKey: ['types'],
    entityPath: entities.types.get,
    baseServiceGet,
    fieldId: 'filter.type_id',
    formData,
    setFormData,
    isMulti: true,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    mapToOption: (item) => ({
      value: item.id,
      label: item.name,
    }),
  })

  const groupsOptions = useRemoteSelectOptions({
    queryKey: ['groups'],
    entityPath: entities.groups.get,
    baseServiceGet,
    extraFilters: {
      'types.id': {
        $in: formData.filter.type_id?.map((id) => id.value) ?? [],
      },
    },
    fieldId: 'filter.group_id',
    formData,
    setFormData,
    isMulti: true,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    mapToOption: (item) => ({
      value: item.id,
      label: item.name,
    }),
  })

  const brandsOptions = useRemoteSelectOptions({
    queryKey: ['groups'],
    entityPath: entities.brands.get,
    baseServiceGet,
    fieldId: 'filter.brand_id',
    formData,
    setFormData,
    isMulti: true,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    mapToOption: (item) => ({
      value: item.id,
      label: item.name,
    }),
  })

  useEffect(() => {
    const hasFilledValidate =
      documentTypeOptions.selectedValue?.value ||
      (formData.filter.start_date && formData.filter.end_date)
    setHasFilled(hasFilledValidate)
  }, [formData])

  const selects = {
    'filter.document_type_id': documentTypeOptions,
    'filter.supplier_id': supplierSelect,
    'filter.product_id': productSelect,
    'filter.type_id': typesOptions,
    'filter.group_id': groupsOptions,
    'filter.brand_id': brandsOptions,
  }

  const getSelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      if (!selectConfig) return {}

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      }
    },
    [selects]
  )

  const getOptions = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      return selectConfig?.options || []
    },
    [selects]
  )

  const getSelectedValue = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      return selectConfig?.selectedValue || null
    },
    [selects]
  )

  const exportReport = useMutation({
    mutationFn: async (type) => {
      showLoader()
      const formated = cleanPayload(formData.filter)
      const params = {
        filters: {
          ...formated,
          document_type_id: formated.document_type_id?.map(
            (docType) => docType.value
          ),
          supplier_id: formated.supplier_id?.map((supplier) => supplier.value),
          product_id: formated.product_id?.map((product) => product.value),
          type_id: formated.type_id?.map((type) => type.value),
          group_id: formated.group_id?.map((group) => group.value),
          brand_id: formated.brand_id?.map((brand) => brand.value),
          organization_id: selectedOrgan?.id,
        },
        sort: { column: 'number', order: 'desc' },
      }

      const response = await baseServiceGet(
        entities.reports.productExtract.get,
        params,
        {
          type,
        }
      )
      return response
    },
    onMutate: () => {
      showLoader()
    },
    onSuccess: (response) => {
      if (response?.status === 202 || response?.status === 200) {
        const generated_id = response.result?.export_id

        if (generated_id) {
          setExportId(generated_id)
          localStorage.setItem('report_exportId', generated_id.toString())
          showNotification(
            'info',
            'Processamento do relatório iniciado em segundo plano.'
          )
        }
      }
    },
    onError: (error) => {
      console.error('onError ', error)
      hideLoader()
    },
    onSettled: () => {
      hideLoader()
    },
  })

  useEffect(() => {
    if (location.state?.message) {
      showNotification('error', location.state.message)
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state, showNotification, navigate, location.pathname])

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader title="Extrato de Produtos" showBackButton={false} />
      <Alert
        severity="info"
        sx={{
          mb: 2,
        }}>
        <strong>Atenção:</strong> Esse relatório apresenta o extrato de
        movimentações de produtos.
      </Alert>
      <div className="container-fluid p-1">
        <Form
          initialFormData={formData}
          onSubmit={() => {}}
          textSubmit="Filtrar"
          textLoadingSubmit="Filtrando..."
          showButtons={false}
          disabledSubmitButton={true}>
          {() => (
            <>
              <div key={filtersSection.section}>
                <SectionHeader section={filtersSection.section} />

                <SimpleBody
                  fields={filtersSection.fields}
                  formErrors={{}}
                  formData={formData}
                  handleFieldChange={handleChange}
                  getOptions={getOptions}
                  getSelectedValue={getSelectedValue}
                  getSelectMeta={getSelectMeta}
                />
              </div>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  my: 4,
                  width: '100%',
                }}>
                <Button
                  variant="outlined"
                  color="primary"
                  type="button"
                  startIcon={<FontAwesomeIcon icon={faFilePdf} />}
                  text="Exportar em PDF"
                  disabled={!hasFilled || isExporting}
                  onClick={async () => {
                    try {
                      await exportReport.mutateAsync('pdf')
                    } catch (error) {
                      console.error('Erro ao exportar PDF:', {
                        status: error.status,
                        message: error.message,
                      })
                    }
                  }}
                />
              </Box>
            </>
          )}
        </Form>
      </div>
    </MainLayout>
  )
}

export default ProductExtractReport
